import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Box, Button, Card, Divider, Typography, useMediaQuery, useTheme } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import { DataGrid } from "@mui/x-data-grid";

import { useFinalizarEntrada, useGestorCola, usePreferenciasGestor, useRutaMostrador } from "../hooks";
import { generateEntradaColumns } from "../helpers/columnsEntradas";
import { resolverEmpleado } from "../helpers/entradas";
import { cerrarModal, crearModalInicial } from "../helpers/modales";
import { ESTADO_INICIAL_GRID } from "../helpers/gridProps";
import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { EntradasMobileList, ModalFinalizarEntrada, ToolbarGestor } from "../components";
import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";

const MODAL_INICIAL = crearModalInicial("entrada");
const TOAST_SIN_RUTA = { tipo: "warning", mensaje: "No hay ruta de mostrador configurada." };

// "Añadido a la ruta" solo si el alta se hizo de verdad (sin avisos: ni sin ruta, ni SP, ni fallo)
const textoFinalizado = (numero, anadidaARuta) =>
    anadidaARuta
        ? `Finalizado el número: ${numero}. Añadido a la ruta de mostrador.`
        : `Finalizado el número: ${numero}.`;

/**
 * Página del gestor de llamamientos (/colas/:cola/gestor): llama, salta y
 * finaliza los turnos de la cola indicada en ?id=. Orquestador: los hooks
 * aportan la ruta de mostrador, las preferencias del puesto, la cola y la
 * finalización con alta en ruta; esta página solo compone y traduce a toasts.
 * El rol ADMIN se resuelve en routes.js. Carga única, sin tiempo real.
 */
const GestorLlamamientos = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const { search } = useLocation();
    const id = useMemo(() => new URLSearchParams(search).get("id"), [search]);
    const { state, updateState } = useContext(StateContext);

    const ruta = useRutaMostrador();
    const { rutaMostrador, transportista } = ruta;
    const prefs = usePreferenciasGestor();
    const gestor = useGestorCola({ id, rutaMostrador, rutaCargando: ruta.cargando });
    const { finalizando, finalizar } = useFinalizarEntrada();
    const [modal, setModal] = useState(MODAL_INICIAL);
    const avisadoSinRuta = useRef(false);

    // Varios toasts de una acción se añaden de una vez: el estado capturado es el mismo
    const mostrarToasts = useCallback(
        (toasts) => {
            const nuevos = toasts.map(({ tipo, mensaje }) => Toasts.generateToast(mensaje, tipo));
            updateState({ toasts: [...state.toasts, ...nuevos] });
        },
        [state.toasts, updateState]
    );

    // Una sola vez, y sin parpadeo: solo cuando la ruta ya terminó de resolverse
    useEffect(() => {
        if (ruta.cargando || rutaMostrador || avisadoSinRuta.current) return;
        avisadoSinRuta.current = true;
        mostrarToasts([ruta.error ? { tipo: "error", mensaje: ruta.error } : TOAST_SIN_RUTA]);
    }, [ruta.cargando, ruta.error, rutaMostrador, mostrarToasts]);

    const bloqueado = gestor.loading || !gestor.cola || gestor.procesandoId !== null || finalizando;
    const sinRuta = !ruta.cargando && !rutaMostrador;

    const ejecutarAccion = useCallback(
        async (accion, textoOk, tipoOk, avisar404 = false) => {
            try {
                const entrada = await accion();
                if (entrada) mostrarToasts([{ tipo: tipoOk, mensaje: `${textoOk} ${entrada.numero}` }]);
            } catch (err) {
                console.error("Error en la acción del gestor:", err);
                // Solo "llamar siguiente": el 404 es "No hay más en espera", un aviso y no un fallo
                const noHayMas = avisar404 && err.status === 404;
                mostrarToasts([{ tipo: noHayMas ? "warning" : "error", mensaje: err.message }]);
            }
        },
        [mostrarToasts]
    );

    const { llamarSiguiente, llamar, saltar, actualizarEntrada } = gestor;
    const { mostrador, empleadoId, empleados } = prefs;

    const handleLlamarSiguiente = useCallback(() => {
        if (!rutaMostrador || !empleadoId) return;
        ejecutarAccion(() => llamarSiguiente(mostrador), "LLamado el número:", "success", true);
    }, [rutaMostrador, empleadoId, mostrador, llamarSiguiente, ejecutarAccion]);

    const handleLlamar = useCallback(
        (entrada) => ejecutarAccion(() => llamar(entrada.id, mostrador), "Llamando el número:", "success"),
        [mostrador, llamar, ejecutarAccion]
    );
    const handleSaltar = useCallback(
        (entrada) => ejecutarAccion(() => saltar(entrada.id), "Saltado el número:", "warning"),
        [saltar, ejecutarAccion]
    );
    const handleAbrirFinalizar = useCallback((entrada) => setModal({ open: true, entrada }), []);
    const handleCerrarFinalizar = useCallback(() => setModal(cerrarModal), []);

    // Finalizar es siempre lo primero; si falla el alta en ruta la entrada queda finalizada con aviso
    const handleConfirmarFinalizar = useCallback(
        async (anadirARuta) => {
            try {
                const resultado = await finalizar(modal.entrada, {
                    anadirARuta,
                    rutaMostrador,
                    transportista,
                    empleado: resolverEmpleado(empleados, empleadoId),
                    year: state.year,
                    onFinalizada: actualizarEntrada,
                });
                if (!resultado) return;
                const { entradaActualizada, avisos } = resultado;
                const exito = textoFinalizado(entradaActualizada.numero, anadirARuta && avisos.length === 0);
                mostrarToasts([...avisos, { tipo: "success", mensaje: exito }]);
            } catch (err) {
                console.error("Error al finalizar entrada:", err);
                mostrarToasts([{ tipo: "error", mensaje: err.message }]);
            } finally {
                setModal(cerrarModal);
            }
        },
        [modal.entrada, finalizar, rutaMostrador, transportista, empleados, empleadoId, state.year, actualizarEntrada, mostrarToasts]
    );

    const columns = useMemo(
        () =>
            generateEntradaColumns({
                onLlamar: handleLlamar,
                onFinalizar: handleAbrirFinalizar,
                onSaltar: handleSaltar,
                procesandoId: gestor.procesandoId,
                bloqueado,
            }),
        [handleLlamar, handleAbrirFinalizar, handleSaltar, gestor.procesandoId, bloqueado]
    );

    const renderContenido = () => {
        if (!id) return <Alert severity="info">Falta el identificador de la cola</Alert>;
        if (sinRuta) {
            return (
                <Alert severity="warning">
                    No se puede cargar la cola: no existe una ruta de mostrador configurada
                </Alert>
            );
        }
        if (isMobile) {
            return (
                <EntradasMobileList
                    entradas={gestor.entradas}
                    loading={gestor.loading}
                    onLlamar={handleLlamar}
                    onFinalizar={handleAbrirFinalizar}
                    onSaltar={handleSaltar}
                    procesandoId={gestor.procesandoId}
                    bloqueado={bloqueado}
                />
            );
        }
        return (
            <DataGrid
                rows={gestor.entradas}
                columns={columns}
                localeText={DATA_GRID_LOCALE_TEXT}
                autoHeight
                loading={gestor.loading}
                disableRowSelectionOnClick
                initialState={ESTADO_INICIAL_GRID}
                pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                slotProps={DATA_GRID_SLOT_PROPS}
                sx={DATA_GRID_SX}
            />
        );
    };

    return (
        <Box id="colas-gestor-llamamientos" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <CampaignIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Gestor de llamamientos
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        {gestor.cola?.nombre ? `Cola: ${gestor.cola.nombre}` : "Llama, salta y finaliza los turnos del mostrador"}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <ToolbarGestor
                        hayRuta={ruta.cargando || Boolean(rutaMostrador)}
                        bloqueado={bloqueado}
                        empleados={empleados}
                        empleadoId={empleadoId}
                        mostrador={mostrador}
                        onLlamarSiguiente={handleLlamarSiguiente}
                        onEmpleadoChange={prefs.setEmpleadoId}
                        onMostradorChange={prefs.setMostrador}
                    />
                    {gestor.error && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Alert
                                severity="error"
                                action={
                                    <Button color="inherit" size="small" onClick={gestor.recargar} disabled={gestor.loading}>
                                        Reintentar
                                    </Button>
                                }
                            >
                                No se pudo cargar la cola. Intenta nuevamente. ({gestor.error})
                            </Alert>
                        </>
                    )}
                </Box>

                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>{renderContenido()}</Box>
            </Card>

            <ModalFinalizarEntrada
                show={modal.open}
                onHide={handleCerrarFinalizar}
                onConfirm={handleConfirmarFinalizar}
                entrada={modal.entrada}
                empleadoId={empleadoId}
                fechaRuta={rutaMostrador?.fecha}
                finalizando={finalizando}
            />
        </Box>
    );
};

export default GestorLlamamientos;
